// src/components/InvoiceDocsPage.jsx
// @ts-nocheck
import React, { useState } from "react";
import { motion } from "framer-motion";

const C = {
  bg: "#07090f",
  surface: "rgba(255,255,255,.03)",
  border: "rgba(255,255,255,.07)",
  text: "#e2e8f0",
  muted: "#475569",
  blue: "#60a5fa",
  green: "#4ade80",
  amber: "#fbbf24",
  red: "#f87171",
  purple: "#a78bfa",
  indigo: "#818cf8",
};

function Badge({ color, children }) {
  const colors = {
    green: { bg: "rgba(74,222,128,.1)", border: "rgba(74,222,128,.2)", text: "#4ade80" },
    blue:  { bg: "rgba(96,165,250,.1)", border: "rgba(96,165,250,.2)", text: "#60a5fa" },
    amber: { bg: "rgba(251,191,36,.1)", border: "rgba(251,191,36,.2)", text: "#fbbf24" },
    red:   { bg: "rgba(248,113,113,.1)", border: "rgba(248,113,113,.2)", text: "#f87171" },
    purple:{ bg: "rgba(167,139,250,.1)", border: "rgba(167,139,250,.2)", text: "#a78bfa" },
  };
  const s = colors[color] || colors.blue;
  return (
    <span style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, fontFamily: "monospace" }}>
      {children}
    </span>
  );
}

function CodeBlock({ code, lang }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ position: "relative", background: "rgba(0,0,0,.4)", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, background: "rgba(255,255,255,.02)" }}>
        <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>{lang}</span>
        <button onClick={copy} style={{ fontSize: 11, color: copied ? C.green : C.muted, background: "none", border: "none", cursor: "pointer" }}>
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <pre style={{ margin: 0, padding: "14px 16px", fontSize: 12, color: "#cbd5e1", fontFamily: "monospace", overflowX: "auto", lineHeight: 1.7 }}>{code}</pre>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 16, paddingBottom: 10, borderBottom: `1px solid ${C.border}` }}>{title}</h2>
      {children}
    </motion.div>
  );
}

const JS_EXAMPLE = `const fs = require('fs');
const imageBuffer = fs.readFileSync('invoice.jpg');
const base64 = imageBuffer.toString('base64');

const response = await fetch('https://orionintelligence.net/api/scan-invoice', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': 'YOUR_API_KEY'
  },
  body: JSON.stringify({
    imageData: base64,
    mediaType: 'image/jpeg',
    isPdf: false
  })
});

const result = await response.json();

if (result.ok) {
  // Αυτόματη εισαγωγή στο ERP
  importToERP(result.data);
} else if (result.requires_review) {
  // Χαμηλή εμπιστοσύνη — χειροκίνητος έλεγχος
  flagForReview(result.partial_data, result.missing_fields);
}`;

const PY_EXAMPLE = `import base64, requests

with open('invoice.jpg', 'rb') as f:
    image_b64 = base64.b64encode(f.read()).decode('utf-8')

response = requests.post(
    'https://orionintelligence.net/api/scan-invoice',
    headers={
        'Content-Type': 'application/json',
        'x-api-key': 'YOUR_API_KEY'
    },
    json={
        'imageData': image_b64,
        'mediaType': 'image/jpeg',
        'isPdf': False
    }
)

result = response.json()
if result['ok']:
    data = result['data']
    print(f"Vendor: {data['vendor']}, Total: {data['total']}")`;

const SUCCESS_RESPONSE = `{
  "ok": true,
  "confidence": 0.95,
  "data": {
    "vendor": "ΑΒΕΕ Τρόφιμα ΕΠΕ",
    "vendor_vat": "123456789",
    "invoice_number": "ΤΙΜ-2025-0041",
    "date": "07/04/2025",
    "due_date": "21/04/2025",
    "subtotal": 100.00,
    "vat_rate": 24,
    "vat_amount": 24.00,
    "total": 124.00,
    "currency": "EUR",
    "payment_method": "Τραπεζική μεταφορά",
    "lines": [
      {
        "description": "Ντομάτες Α' ποιότητας",
        "quantity": 10,
        "unit": "kg",
        "unit_price": 1.20,
        "line_total": 12.00,
        "vat_rate": 24
      }
    ],
    "notes": null
  }
}`;

const REVIEW_RESPONSE = `{
  "ok": false,
  "error": "Low confidence or missing required fields — human review required",
  "confidence": 0.65,
  "missing_fields": ["vendor", "total"],
  "partial_data": { ...ότι βρέθηκε... },
  "requires_review": true
}`;

export default function InvoiceDocsPage() {
  return (
    <div style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "Inter, sans-serif" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg,transparent,#3b82f6 30%,#6366f1 60%,transparent)" }} />

      {/* Header */}
      <div style={{ borderBottom: `1px solid ${C.border}`, padding: "18px 24px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#3b82f6,#6366f1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "white" }}>OI</div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Invoice Intelligence API</div>
          <div style={{ fontSize: 11, color: C.muted }}>Orion Intelligence — API Documentation v1.0</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Badge color="green">v1.0</Badge>
          <Badge color="blue">REST</Badge>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 24px" }}>

        {/* Intro */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 10 }}>Invoice Intelligence API</h1>
          <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.8, maxWidth: 640 }}>
            AI-powered ανάλυση τιμολογίων σε δευτερόλεπτα. Στείλε μια εικόνα ή PDF — πάρε δομημένα ERP-ready δεδομένα. Ενσωμάτωσε σε οποιοδήποτε σύστημα με 10 γραμμές κώδικα.
          </p>
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <Badge color="purple">PDF</Badge><Badge color="purple">JPG</Badge><Badge color="purple">PNG</Badge>
            <Badge color="blue">Vendor</Badge><Badge color="blue">ΑΦΜ</Badge><Badge color="blue">Γραμμές προϊόντων</Badge>
            <Badge color="blue">ΦΠΑ</Badge><Badge color="blue">Σύνολα</Badge>
            <Badge color="green">ERP-ready JSON</Badge>
          </div>
        </motion.div>

        {/* Base URL */}
        <Section title="Base URL & Authentication">
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Base URL</div>
            <code style={{ fontSize: 14, color: C.blue }}>https://orionintelligence.net/api</code>
          </div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>Authentication — HTTP Header</div>
            <code style={{ fontSize: 13, color: C.amber }}>x-api-key: YOUR_API_KEY</code>
            <p style={{ fontSize: 12, color: C.muted, marginTop: 10, marginBottom: 0 }}>
              Για να αποκτήσεις API key επικοινώνησε στο{" "}
              <a href="mailto:support@orionintelligence.net" style={{ color: C.blue }}>support@orionintelligence.net</a>
            </p>
          </div>
        </Section>

        {/* Endpoint */}
        <Section title="POST /api/scan-invoice">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <Badge color="green">POST</Badge>
            <code style={{ fontSize: 13, color: C.text }}>/api/scan-invoice</code>
          </div>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 16, lineHeight: 1.7 }}>
            Αναλύει ένα τιμολόγιο και επιστρέφει δομημένα δεδομένα. Αν η εμπιστοσύνη είναι κάτω από 80% ή λείπουν κρίσιμα πεδία, επιστρέφει <code style={{ color: C.amber }}>requires_review: true</code> αντί για αυτόματη εισαγωγή.
          </p>

          {/* Request */}
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>Request Body (JSON)</div>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
            {[
              { field: "imageData", type: "string", req: true, desc: "Base64 encoded εικόνα ή PDF" },
              { field: "mediaType", type: "string", req: true, desc: "image/jpeg · image/png · application/pdf" },
              { field: "isPdf",     type: "boolean", req: true, desc: "true αν το αρχείο είναι PDF" },
            ].map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "160px 80px 60px 1fr", gap: 12, padding: "10px 16px", borderBottom: i < 2 ? `1px solid ${C.border}` : "none", alignItems: "center" }}>
                <code style={{ fontSize: 12, color: C.blue }}>{r.field}</code>
                <code style={{ fontSize: 11, color: C.purple }}>{r.type}</code>
                <Badge color={r.req ? "red" : "amber"}>{r.req ? "required" : "optional"}</Badge>
                <span style={{ fontSize: 12, color: C.muted }}>{r.desc}</span>
              </div>
            ))}
          </div>

          {/* Responses */}
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>Responses</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
            {[
              { code: "200", color: "green", desc: "Επιτυχία — ERP-ready δεδομένα" },
              { code: "422", color: "amber", desc: "Χαμηλή εμπιστοσύνη — απαιτείται χειροκίνητος έλεγχος" },
              { code: "401", color: "red", desc: "Invalid API key" },
              { code: "400", color: "red", desc: "Missing required fields" },
              { code: "500", color: "red", desc: "Internal server error" },
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                <Badge color={r.color}>{r.code}</Badge>
                <span style={{ fontSize: 12, color: C.muted }}>{r.desc}</span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4 }}>200 — Success Response</div>
          <CodeBlock code={SUCCESS_RESPONSE} lang="json" />
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 4, marginTop: 16 }}>422 — Review Required</div>
          <CodeBlock code={REVIEW_RESPONSE} lang="json" />
        </Section>

        {/* Code examples */}
        <Section title="Παραδείγματα ενσωμάτωσης">
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>JavaScript / Node.js</div>
          <CodeBlock code={JS_EXAMPLE} lang="javascript" />
          <div style={{ fontSize: 13, color: C.muted, marginTop: 20, marginBottom: 12 }}>Python</div>
          <CodeBlock code={PY_EXAMPLE} lang="python" />
        </Section>

        {/* Pricing */}
        <Section title="Τιμολόγηση">
          <div style={{ background: "rgba(99,102,241,.05)", border: "1px solid rgba(99,102,241,.15)", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.indigo, marginBottom: 8 }}>Usage-based τιμολόγηση</div>
            <p style={{ fontSize: 13, color: C.muted, lineHeight: 1.8, marginBottom: 16 }}>
              Χρεώνεσαι ανά scan — πληρώνεις μόνο ό,τι χρησιμοποιείς. Κατάλληλο για ενσωμάτωση σε ERP, λογιστικά και WMS συστήματα.
            </p>
            <a href="mailto:support@orionintelligence.net?subject=Invoice Intelligence API - Τιμολόγηση"
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(99,102,241,.15)", color: C.indigo, border: "1px solid rgba(99,102,241,.25)", borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 500, textDecoration: "none" }}>
              ✉ Επικοινωνία για τιμολόγηση
            </a>
          </div>
        </Section>

      </div>
    </div>
  );
}
